import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

import classNames from "classnames";
import useClickedOutside from "../../hooks/useClickedOutside";
import IconButton from "../IconButton";
import ProgressContainer from "../ProgressContainer";
import Text from "../Text";
import TextButton from "../TextButton";
import { AddBlankElementContainer, OptionsContainer, RelativeContainer } from "./styles";

export interface SplitButtonOption {
  label: string;
  value: string;
}

interface SplitButtonProps {
  onClick: (option: SplitButtonOption | null) => void;
  onSelect?: (option: SplitButtonOption) => void;
  options?: SplitButtonOption[];
  disabled?: boolean;
  children?: ReactNode;
  defaultOption?: SplitButtonOption;
  dropdownMaxHeight?: number | string;
  isLoading?: boolean;
  duration?: number;
}

export default function SplitButton({ onClick, onSelect, options, disabled, children, defaultOption, dropdownMaxHeight, isLoading, duration }: SplitButtonProps) {
  const [selectedOption, setSelectedOption] = useState<SplitButtonOption | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [clampedMaxHeight, setClampedMaxHeight] = useState<string | undefined>(undefined);

  const hideDropdown = useCallback(() => {
    setShowDropdown(false);
  }, []);
  const containerRef = useClickedOutside(hideDropdown);

  const handleOnSelect = useCallback(
    (option: SplitButtonOption) => {
      hideDropdown();
      setSelectedOption(option);
      if (onSelect) onSelect(option);
    },
    [hideDropdown, onSelect]
  );

  const handleOnClick = useCallback(() => {
    onClick(selectedOption);
  }, [onClick, selectedOption]);

  const dropdownRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (options?.length && options.length > 0) handleOnSelect(defaultOption || options.at(0)!);
  }, []);

  // Clamp dropdown maxHeight to viewport when dropdown opens
  useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const available = window.innerHeight - rect.top - 20; // 20px bottom margin
      const parsed = typeof dropdownMaxHeight === "number"
        ? dropdownMaxHeight
        : parseFloat(dropdownMaxHeight as string);
      const maxH = !isNaN(parsed) ? Math.min(available, parsed) : available;
      setClampedMaxHeight(Math.max(maxH, 0) + "px");
    } else {
      setClampedMaxHeight(undefined);
    }
  }, [showDropdown, dropdownMaxHeight]);

  // Focus first option when dropdown opens
  useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      requestAnimationFrame(() => {
        const firstButton = dropdownRef.current?.querySelector<HTMLButtonElement>("button");
        if (firstButton) firstButton.focus();
      });
    }
  }, [showDropdown]);

  // Close on Escape and handle arrow key navigation (element-level so stopPropagation
  // prevents the event from reaching document-level listeners like usePopoverClose)
  const handleDropdownKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      hideDropdown();
      const container = containerRef.current as HTMLElement | null;
      const trigger = container?.querySelector<HTMLElement>(".icon-button-wrapper");
      trigger?.focus();
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const buttons = dropdownRef.current?.querySelectorAll<HTMLButtonElement>("button");
      if (!buttons?.length) return;
      const currentIndex = Array.from(buttons).indexOf(e.target as HTMLButtonElement);
      const nextIndex = e.key === "ArrowDown"
        ? (currentIndex + 1) % buttons.length
        : (currentIndex - 1 + buttons.length) % buttons.length;
      buttons[nextIndex].focus();
    }
  }, [hideDropdown]);

  return (
    <RelativeContainer
      ref={containerRef}
      id="split-button-relative-container"
    >
      <ProgressContainer
        isLoading={isLoading}
        duration={duration}
        style={{ borderRadius: "10px" }}
      >
        <AddBlankElementContainer
          className={`${disabled ? "disabled" : ""} ${isLoading ? "nohover" : ""}`}
          id="split-button-add-blank-element-container"
        >
          <TextButton
            onClick={handleOnClick}
            disabled={disabled}
            src="/assets/chain-linked.svg"
          >
            {children}
          </TextButton>
          <IconButton
            triggerClassName="h-full"
            size={22}
            disabled={disabled}
            onClick={() => setShowDropdown((prevState) => !prevState)}
            src="/assets/down-chevron-24.svg"
            aria-label="Select document"
            aria-expanded={showDropdown}
          />
        </AddBlankElementContainer>
      </ProgressContainer>
      <OptionsContainer ref={dropdownRef} role="menu" onKeyDown={handleDropdownKeyDown} style={{ maxHeight: !showDropdown ? 0 : clampedMaxHeight ?? dropdownMaxHeight, opacity: showDropdown ? 1 : 0, visibility: showDropdown ? "visible" : "hidden" }}>
        {options?.map((option, i) => (
          <li
            key={i}
            className="option-item"
          >
            <OptionButton
              className="use-component-styles"
              onClick={handleOnSelect}
              selected={option.value === selectedOption?.value}
              option={option}
            />
          </li>
        ))}
      </OptionsContainer>
    </RelativeContainer>
  );
}

interface OptionButtonProps {
  className?: string;
  onClick: (option: SplitButtonOption) => void;
  selected: boolean;
  option: SplitButtonOption;
}

const OptionButton = ({ onClick, option, selected }: OptionButtonProps) => {
  const handleOnClick = useCallback(() => {
    onClick(option);
  }, [onClick, option]);

  return (
    <button
      className={classNames("option-item-button use-component-styles", {
        selected: selected,
      })}
      onClick={handleOnClick}
    >
      <Text truncate>{option.label}</Text>
    </button>
  );
};
